const Mutations = Object.freeze({
	SET_INITIALIZED: 'grids/SET_INITIALIZED',
	SET_ENTITIES: 'grids/SET_ENTITIES',
	SET_REFERENCE_LIST_TYPES: 'grids/SET_REFERENCE_LIST_TYPES',
	SET_REFERENCE_LIST_VALUES: 'grids/SET_REFERENCE_LIST_VALUES',
	SET_DATA_FIELDS: 'grids/SET_DATA_FIELDS',
	SET_GRID_VIEWS: 'grids/SET_GRID_VIEWS',
	SET_SELECTED_ENTITY_ID: 'grids/SET_SELECTED_ENTITY_ID',
});

export const Actions = Object.freeze({
	LOAD_GRIDS: 'grids/LOAD_GRIDS',
	RELOAD_ENTITIES: 'grids/RELOAD_ENTITIES',
	RELOAD_GRID_VIEWS: 'grids/RELOAD_GRID_VIEWS',
	RELOAD_REFERENCE_LIST_TYPES: 'grids/RELOAD_REFERENCE_LIST_TYPES',
	RELOAD_REFERENCE_LIST_VALUES: 'grids/RELOAD_REFERENCE_LIST_VALUES',
	SET_SELECTED_ENTITY_ID: 'grids/SET_SELECTED_ENTITY_ID',
	DOWNLOAD_FILE: 'grids/DOWNLOAD_FILE',
	PREVIEW_FILE: 'grids/PREVIEW_FILE',
	CLEAR: 'grids/CLEAR',
});

export const Getters = Object.freeze({
	INITIALIZED: 'grids/INITIALIZED',
	GET_ENTITIES: 'grids/GET_ENTITIES',
	GET_DATA_FIELDS: 'grids/GET_DATA_FIELDS',
	GET_REFERENCE_LIST_TYPES: 'grids/GET_REFERENCE_LIST_TYPES',
	GET_REFERENCE_LIST_VALUES: 'grids/GET_REFERENCE_LIST_VALUES',
	GET_MENU_ENTITIES: 'grids/GET_MENU_ENTITIES',
	GET_GRID_VIEWS: 'grids/GET_GRID_VIEWS',
	GET_LINK_FIELD_DATA_FIELDS: 'grids/GET_LINK_FIELD_DATA_FIELDS',
});

import { GeneralActions } from '../../../../actions';
import dataService from '../../../../../services/data/projectGridsService';
import gridFieldsSettings from './gridFieldsSettings';
import gridsPage from './gridsPage';
import gridsPageEdit from './gridsPageEdit';
import gridsRecordHistory from './gridsRecordHistory';
import gridsHistory from './gridsHistory';
import gridsPageDeleted from './gridsPageDeleted';
import { gridViewTypes } from '../../../../../enums/gridViewTypes';
import userContextDataService from '../../../../../services/data/userContextDataService';
import fileUtils from '../../../../../utils/fileUtils';

export default {
	state: {
		initialized: false,
		selectedEntityId: null,
		entities: [],
		referenceListTypes: [],
		referenceListValues: [],
		dataFields: [],
		gridViews: [],
	},
	modules: {
		gridFieldsSettings,
		gridsPage,
		gridsPageEdit,
		gridsHistory,
		gridsRecordHistory,
		gridsPageDeleted,
	},
	mutations: {
		[Mutations.SET_INITIALIZED](state, value) {
			state.initialized = value;
		},
		[Mutations.SET_ENTITIES](state, items) {
			state.entities = items;
		},
		[Mutations.SET_DATA_FIELDS](state, items) {
			state.dataFields = items;
		},
		[Mutations.SET_REFERENCE_LIST_TYPES](state, items) {
			state.referenceListTypes = items;
		},
		[Mutations.SET_REFERENCE_LIST_VALUES](state, items) {
			state.referenceListValues = items;
		},
		[Mutations.SET_GRID_VIEWS](state, items) {
			state.gridViews = items;
		},
		[Mutations.SET_SELECTED_ENTITY_ID](state, id) {
			state.selectedEntityId = id;
		},
	},
	actions: {
		[Actions.DOWNLOAD_FILE]({ dispatch }, id) {
			dispatch(GeneralActions.LOADING, true);
			return new Promise((resolve) => {
				userContextDataService
					.downloadFile(id)
					.then((resp) => {
						const fileInfo =
							fileUtils.extractFileInfoFromResponse(resp);
						const blob = new Blob([resp.data], {
							type: fileInfo.mimeType,
						});
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.href = url;
						link.setAttribute('download', fileInfo.filename);
						document.body.appendChild(link);
						link.click();

						// Clean up resources
						document.body.removeChild(link);
						window.URL.revokeObjectURL(url);

						resolve(true);
					})
					.catch((error) => {
						dispatch(GeneralActions.ERROR, error);
						resolve(null);
					})
					.finally(() => {
						dispatch(GeneralActions.LOADING, false);
					});
			});
		},

		[Actions.LOAD_GRIDS]({ commit, dispatch }) {
			return new Promise((resolve) => {
				Promise.all([
					dataService.getEntities().then((resp) => {
						commit(Mutations.SET_ENTITIES, resp.data);
					}),
					dataService.getDataFields().then((resp) => {
						commit(Mutations.SET_DATA_FIELDS, resp.data);
					}),
					dataService.getReferenceListTypes().then((resp) => {
						commit(Mutations.SET_REFERENCE_LIST_TYPES, resp.data);
					}),
					dataService.getReferenceListValues().then((resp) => {
						commit(Mutations.SET_REFERENCE_LIST_VALUES, resp.data);
					}),
					dataService.getGridViews().then((resp) => {
						commit(Mutations.SET_GRID_VIEWS, resp.data);
					}),
				])
					.then(() => {
						commit(Mutations.SET_INITIALIZED, true);
						resolve();
					})
					.catch((error) => {
						dispatch(GeneralActions.ERROR, error);
						resolve();
					});
			});
		},

		[Actions.RELOAD_ENTITIES]({ commit }) {
			return Promise.all([
				dataService.getEntities().then((resp) => {
					commit(Mutations.SET_ENTITIES, resp.data);
				}),
				dataService.getDataFields().then((resp) => {
					commit(Mutations.SET_DATA_FIELDS, resp.data);
				}),
			]);
		},
		[Actions.RELOAD_REFERENCE_LIST_TYPES]({ commit }) {
			return Promise.all([
				dataService.getReferenceListTypes().then((resp) => {
					commit(Mutations.SET_REFERENCE_LIST_TYPES, resp.data);
				}),
			]);
		},

		[Actions.RELOAD_REFERENCE_LIST_VALUES]({ commit }) {
			return Promise.all([
				dataService.getReferenceListValues().then((resp) => {
					commit(Mutations.SET_REFERENCE_LIST_VALUES, resp.data);
				}),
			]);
		},

		[Actions.RELOAD_GRID_VIEWS]({ commit }) {
			return Promise.all([
				dataService.getGridViews().then((resp) => {
					commit(Mutations.SET_GRID_VIEWS, resp.data);
				}),
			]);
		},

		[Actions.SET_SELECTED_ENTITY_ID]({ commit }, id) {
			return new Promise((resolve) => {
				commit(Mutations.SET_SELECTED_ENTITY_ID, id);
				resolve();
			});
		},
		[Actions.CLEAR]({ commit }) {
			return new Promise((resolve) => {
				commit(Mutations.SET_REFERENCE_LIST_VALUES, []);
				commit(Mutations.SET_REFERENCE_LIST_TYPES, []);
				commit(Mutations.SET_DATA_FIELDS, []);
				commit(Mutations.SET_ENTITIES, []);
				commit(Mutations.SET_INITIALIZED, false);
				commit(Mutations.SET_SELECTED_ENTITY_ID, null);
				resolve();
			});
		},
	},
	getters: {
		[Getters.INITIALIZED]: (state) => {
			return state.initialized;
		},
		[Getters.GET_ENTITIES]: (state) => {
			if (state.entities && state.entities.length > 0) {
				return JSON.parse(
					JSON.stringify(
						state.entities.sort((a, b) =>
							a.Name > b.Name ? 1 : -1
						)
					)
				);
			}
			return [];
		},
		[Getters.GET_DATA_FIELDS]: (state) => {
			if (state.dataFields && state.dataFields.length > 0) {
				return JSON.parse(JSON.stringify(state.dataFields));
			}
			return [];
		},
		[Getters.GET_REFERENCE_LIST_TYPES]: (state) => {
			if (
				state.referenceListTypes &&
				state.referenceListTypes.length > 0
			) {
				return JSON.parse(
					JSON.stringify(
						state.referenceListTypes.sort((a, b) =>
							a.Name > b.Name ? 1 : -1
						)
					)
				);
			}
			return [];
		},
		[Getters.GET_REFERENCE_LIST_VALUES]: (state) => {
			if (
				state.referenceListValues &&
				state.referenceListValues.length > 0
			) {
				return JSON.parse(JSON.stringify(state.referenceListValues));
			}
			return [];
		},
		[Getters.GET_MENU_ENTITIES]: (state) => {
			if (state.gridViews && state.gridViews.length > 0) {
				let selectedGridViews = state.gridViews.filter(
					(x) => x.Columns.length > 0
				);
				if (selectedGridViews.length > 0) {
					let entities = selectedGridViews.map((x) => x.Entity);
					let selectedEntities = [];
					entities.forEach((e) => {
						let exists = selectedEntities.find((x) => x.Id == e.Id);
						if (!exists) {
							selectedEntities.push(e);
						}
					});
					return selectedEntities;
				}
			}

			return [];
		},
		[Getters.GET_GRID_VIEWS]: (state) => {
			if (state.gridViews && state.gridViews.length > 0) {
				return JSON.parse(JSON.stringify(state.gridViews));
			}
			return [];
		},
		[Getters.GET_LINK_FIELD_DATA_FIELDS]: (state) => {
			if (state.gridViews && state.gridViews.length > 0) {
				let gridViews = state.gridViews.filter(
					(x) =>
						x.Type == gridViewTypes.Grids &&
						x.UserId == null &&
						x.EntityId != state.selectedEntityId
				);
				let fields = gridViews
					.map((gv) => gv.Columns)
					.flat(1)
					.map((x) => ({
						GvfLabel: x.Label,
						DataField: x.DataField,
					}));

				return JSON.parse(JSON.stringify(fields));
			}
			return [];
		},
	},
};
